/**
 * What the signed-in user may do, from the roles in the Waffy ID token (`authorities`). The service
 * enforces every one of these; the UI only hides what would be refused.
 */
const FULL_ACCESS_ROLES = ["CLIENT_ADMIN", "ORGANIZATION_OWNER"];

const BUSINESS_ROLES = [
  ...FULL_ACCESS_ROLES,
  "CLIENT_ADMIN_CREATE_CONTRACT",
  "CLIENT_ADMIN_UPDATE_CONTRACT",
  "CLIENT_ADMIN_VIEW_CONTRACT",
  "CLIENT_ADMIN_CREATE_USER",
];

export const hasFullAccess = (authorities?: string[]): boolean =>
  !!authorities?.some((role) => FULL_ACCESS_ROLES.includes(role));

/** Can create and view the organization's API clients (service: owner, full admin or developer). */
export const canManageApiClients = (authorities?: string[]): boolean =>
  hasFullAccess(authorities) || !!authorities?.includes("CLIENT_DEVELOPER");

/** A developer with no business permission: sees the API clients, not contracts, users or money. */
export const isDeveloperOnly = (authorities?: string[]): boolean =>
  !!authorities?.includes("CLIENT_DEVELOPER") &&
  !authorities.some((role) => BUSINESS_ROLES.includes(role));

/** May open the organization's projects (the service lists them for these permissions). */
export const canViewContracts = (authorities?: string[]): boolean =>
  hasFullAccess(authorities) ||
  !!authorities?.some((role) =>
    [
      "CLIENT_ADMIN_VIEW_CONTRACT",
      "CLIENT_ADMIN_CREATE_CONTRACT",
      "CLIENT_ADMIN_UPDATE_CONTRACT",
    ].includes(role)
  );

/**
 * Wallet, transactions, statistics, customers, latest updates and the bank and document tabs are
 * answered by the service only for the owner and full admins; for everyone else they are refused,
 * so the portal does not offer them.
 */
export const canViewFinanceAndCustomers = (authorities?: string[]): boolean =>
  hasFullAccess(authorities);

/** Settings, Users: add and edit users. */
export const canManageUsers = (authorities?: string[]): boolean =>
  hasFullAccess(authorities) ||
  !!authorities?.includes("CLIENT_ADMIN_CREATE_USER");
